import "../../global.css";
import "./itens.css";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import { userRequest } from "../../requestMethods";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCompany } from "../../components/context/CompanyContext";
import Loading from "../../components/loading/loading";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { titleSetRef } from "../../redux/titleRedux";
import { setAllFilters } from "../../redux/customGridRedux";
import CustomGrid from "../../components/grid/CustomGrid";
import RowSelect from "../../components/grid/RowSelect";
import { storeScopeOrders } from "../../util/storeScope";
export default function OrderList() {
  const [searchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.user.currentUser);
  const allFilters = useSelector(
    (state) => state.customGrid["orderList"]?.allFilters || [],
  );
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const { companyId, slug } = useCompany();
  const dispatch = useDispatch();
  // Filtro sintético (sem coluna no grid) — aplicado sempre no servidor
  const syntheticFiltersRef = useRef([]);
  const sortRef = useRef(null);
  const dateFilterStart = searchParams.get("start");
  const dateFilterEnd = searchParams.get("end");
  const statusFilter = searchParams.get("status");
  const paymentTypeIds = searchParams.get("paymentTypeIds");
  const storeIdParam = searchParams.get("storeId");
  const from = searchParams.get("from");

  // Buscar tipos de pagamento do backend (apenas para filtros)
  useEffect(() => {
    if (companyId > 0) {
      userRequest
        .get(`${companyId}/paymentTypes`)
        .then((res) => {
          setPaymentTypes(res.data || []);
        })
        .catch(() => {
          setPaymentTypes([]);
        });
    }
  }, [companyId]);

  const status = [
    "Pendente",
    "Confirmado",
    "Preparado",
    "Pago",
    "Enviado",
    "Finalizado",
    "Cancelado",
  ];

  // Opções do filtro: value = id (casa com o filtro paymentTypeId), label = nome
  const paymentTypeOptions = useMemo(
    () => paymentTypes.map((pt) => ({ value: String(pt.paymentTypeId), label: pt.paymentType })),
    [paymentTypes]
  );

  useEffect(() => {
    dispatch(titleSetRef({ title: "Pedidos" }));
  }, [dispatch]);

  useEffect(() => {
    // Reaplica os filtros sempre que os params da URL mudarem (navegação vinda
    // do HomeV2). Sem guard de "carrega uma vez" — senão cliques subsequentes
    // não re-filtram a lista.
    if (companyId > 0) {
      // Construir filtros de data, status e paymentTypeId da URL se existirem
      const filters = [];
      if (dateFilterStart) {
        filters.push({
          field: "createdAt",
          operator: "greaterThanOrEqual",
          value: dateFilterStart,
        });
      }
      if (dateFilterEnd) {
        filters.push({
          field: "createdAt",
          operator: "lessThanOrEqual",
          value: dateFilterEnd,
        });
      }
      if (statusFilter) {
        filters.push({
          field: "status",
          operator: "equals",
          value: statusFilter,
        });
      }

      // Filtro de pagamento por ids reais (vindo do HomeV2) — aplicado como
      // sintético (não vira linha editável no grid, pois o valor é id e poderia
      // representar vários tipos). Filtra no servidor e é mantido na paginação.
      const synthetic = [];
      if (paymentTypeIds) {
        synthetic.push({ field: "paymentTypeId", operator: "in", value: paymentTypeIds });
      }
      syntheticFiltersRef.current = synthetic;

      // Escopo de loja vindo do indicadoresVendas (storeId=43 ou 43,44)
      const storeIdsFromUrl = storeIdParam
        ? storeIdParam.split(",").map((id) => parseInt(id.trim())).filter((id) => !isNaN(id))
        : null;
      if (storeIdsFromUrl) {
        synthetic.push({ field: "_stories", operator: "equals", value: storeIdsFromUrl });
      }
      syntheticFiltersRef.current = synthetic;

      const veioDoDashboard =
        dateFilterStart || dateFilterEnd || statusFilter || paymentTypeIds || storeIdParam;

      if (veioDoDashboard) {
        // Navegação a partir do dashboard: aplica filtros visíveis + sintéticos
        dispatch(setAllFilters({ gridId: "orderList", filters }));
        getOrders(0, pageSize, [...filters, ...synthetic]);
      } else {
        // Navegação normal (menu): preserva filtros já existentes no grid
        getOrders();
      }
    }
  }, [companyId, dispatch, dateFilterStart, dateFilterEnd, statusFilter, paymentTypeIds, storeIdParam]);
  const normalizarParaExport = (item) => {
    const amount = item.amount || 0;
    const discountPct = item.discount || 0;
    const frete = item.delivery?.vlrFrete || 0;
    const devolution = item.devolution || 0;
    const valorLiquido = item.valorLiquido;
    const customer = item.customer || {};

    return {
      orderId: item.orderId,
      orderRef: item.orderRef,
      amount,
      discount: discountPct,
      frete,
      valorLiquido,
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      quantity: item.quantity,
      uniqueId: item.uniqueId,
      name: customer.name,
      contact: customer.contact,
      rzSocial: customer.rzSocial,
      cnpj_cpf: customer.cnpj_cpf,
      city: customer.address?.city || "",
      managedBy: item.managedBy,
      owner_cpf: customer.owner_cpf,
      paymentTypeId: item.paymentTypeId,
      paymentType: item.paymentType || "-",
      paymentAt: item.paymentAt,
      integrationId: item.integrationId,
      devolution: devolution,
      store: item.store || "-",
    };
  };
  const getOrders = async (newPage = page, newPageSize = pageSize, filtersToUse = null) => {
    setIsLoading(true);
    try {
      // filtersToUse já vem completo (efeito inicial); senão usa allFilters do grid
      // + os sintéticos (ex: paymentTypeId in) para preservar na paginação.
      const allApplied =
        filtersToUse !== null
          ? filtersToUse
          : [...allFilters, ...syntheticFiltersRef.current];

      // Extrai _stories (escopo de loja vindo da URL) antes de enviar ao backend
      const storiesFilter = allApplied.find((f) => f.field === "_stories");
      const filters = allApplied.filter((f) => f.field !== "_stories");

      const body = {
        page: newPage,
        limit: newPageSize,
        paginated: true,
        filters,
        ...(storiesFilter ? { stories: storiesFilter.value } : storeScopeOrders(currentUser)),
        ...(sortRef.current && { sort: sortRef.current }),
      };
      const res = await userRequest.post(`${companyId}/orders/find`, body);

      const pedido = res.data.rows.map((item) => normalizarParaExport(item));
      setData(pedido);
      setTotalRows(res.data.totalRecords); // <── total vindo do backend
      setPage(newPage);
      setPageSize(newPageSize);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const nav = useNavigate();
  async function updateStatusOrder(orderId, status) {
    if (currentUser.isAdmin) {
      const res = await userRequest.put(`${companyId}/orders/${orderId}`, {
        status: status,
        orderId: orderId,
        lastUserUpdate: currentUser.userId,
        //changedBy: currentUser.userId,
      });
    } else {
      Swal.fire(
        "Atenção!",
        "Você não tem permissão para editar o status!",
        "warning",
      );
    }
  }
  function onStatusChange(rowId, value) {
    updateStatusOrder(rowId, value);
  }

  const columns = [
    {
      field: "itens",
      type: "text",
      headerName: "Itens",
      width: 60,
      align: "center",
      headerAlign: "center",
      clickable: true,
      renderCell: (params) => {
        return (
          <PictureAsPdfOutlinedIcon
            fontSize="small"
            sx={{ height: "30px", width: "30px" }}
          />
        );
      },
    },
    {
      field: "orderRef",
      type: "text",
      headerName: "Número Pedido",
      width: 140,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "name",
      type: "text",
      headerName: "Nome",
      width: 200,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "owner_cpf",
      type: "text",
      headerName: "CPF proprietário",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "contact",
      type: "text",
      headerName: "Contato",
      width: 120,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "rzSocial",
      type: "text",
      headerName: "Razão Social",
      width: 200,
    },
    {
      field: "cnpj_cpf",
      type: "text",
      headerName: "CNPJ",
      width: 150,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "city",
      type: "text",
      headerName: "Cidade",
      width: 140,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "quantity",
      type: "number",
      headerName: "Qtde Itens",
      width: 60,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "valorLiquido",
      type: "number",
      headerName: "Valor Venda",
      width: 130,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "amount",
      type: "number",
      headerName: "Valor Pedido",
      width: 120,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "discount",
      type: "number",
      headerName: "Desconto",
      width: 120,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "devolution",
      type: "number",
      headerName: "Devolução",
      width: 120,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "frete",
      type: "number",
      headerName: "Frete",
      width: 120,
      align: "center",
      headerAlign: "center",
      format: "BRL",
    },
    {
      field: "managedBy",
      type: "text",
      headerName: "Vendedor(a)",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "store",
      type: "text",
      headerName: "Loja",
      width: 140,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      align: "center",
      headerAlign: "center",
      editable: true,
      type: "text",
      valueOptions: status,
      renderCell: ({ row }) => (
        <RowSelect
          value={row.status}
          rowId={row.orderId}
          options={[
            { value: "Criado", label: "Criado" },
            { value: "Pendente", label: "Pendente" },
            { value: "Confirmado", label: "Confirmado" },
            { value: "Preparado", label: "Preparado" },
            { value: "Pago", label: "Pago" },
            { value: "Enviado", label: "Enviado" },
            { value: "Finalizado", label: "Finalizado" },
            { value: "Cancelado", label: "Cancelado" },
          ]}
          statusColors={{
            Criado: {
              background: "#f4f1d5",
              color: "#a29307",
            },
            Pendente: {
              background: "#f1d1c3",
              color: "#863c1c",
            },
            Confirmado: {
              background: "#f7e0f3",
              color: "#a3298f",
            },
            Preparado: {
              background: "#bdc2e9",
              color: "#040a3e",
            },
            Pago: {
              background: "#c2e0f4",
              color: "#0e67a3",
            },
            Enviado: {
              background: "#e7ccf9",
              color: "#9815ef",
            },
            Finalizado: {
              background: "#c7f6da",
              color: "#06632b",
            },
            Cancelado: {
              background: "rgb(243, 142, 142)",
              color: "red",
            },
          }}
          onChange={onStatusChange}
        />
      ),
    },
    {
      field: "paymentType",
      dbField: "paymentTypeId",
      type: "text",
      headerName: "Opção de Pagamento",
      width: 150,
      align: "left",
      headerAlign: "left",
      valueOptions: paymentTypeOptions,
    },
    {
      field: "paymentTypeId",
      type: "number",
      headerName: "Pago no App",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const statusPago = ["Pago", "Enviado", "Finalizado"];
        if (params.value === 1 && statusPago.includes(params.row.status)) {
          return <CheckCircleOutlineOutlinedIcon color="success" />;
        }
        return "";
      },
    },
    {
      field: "createdAt",
      type: "date",
      headerName: "Criado",
      width: 140,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy HH:mm:ss",
    },
    {
      field: "updatedAt",
      type: "date",
      headerName: "Atualizado",
      width: 140,
      align: "center",
      headerAlign: "center",
      format: "dd/MM/yyyy HH:mm:ss",
    },
    {
      field: "integrationId",
      type: "text",
      headerName: "Integração",
      width: 200,
      align: "left",
      headerAlign: "left",
    },
  ];
  const handlePageChangeServer = (newPage, newPageSize, extra) => {
    if (extra?.sort !== undefined) {
      sortRef.current = extra.sort;
    }
    getOrders(newPage + 1, newPageSize);
  };
  return (
    <div className="cust-page">
      {from && (
        <div
          onClick={() => nav(`/${slug}/${from}`)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            marginBottom: 12, cursor: "pointer",
            color: "#6B7280", fontSize: 13, fontWeight: 600, fontFamily: "Manrope",
            padding: "6px 12px", borderRadius: 8,
            border: "1px solid #E5E7EB", background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
          }}
        >
          ← Voltar para Indicadores
        </div>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <CustomGrid
          gridId="orderList"
          rows={data}
          columns={columns}
          pageSize={pageSize}
          serverSide={true}
          rowCount={totalRows}
          onPageChangeServer={handlePageChangeServer}
          checkboxSelection={false}
          onRowClick={(row) => nav(`${row.orderId}`)}
          clickableCellsOnly={true}
          endpopintExport="/orders/find"
          callExport="post"
          objectExport={normalizarParaExport}
          tableHeight="calc(100vh - 100px)"
        />
      )}
    </div>
  );
}
